import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocaleState, useNotify, useTranslate } from "react-admin";
import { renderToStaticMarkup } from "react-dom/server";
import { Link } from "react-router-dom";

import {
  DeviceEnrollmentTokenStatus,
  RevealDeviceEnrollmentTokenDocument,
  RevealDeviceEnrollmentTokenQuery,
  RevealDeviceEnrollmentTokenQueryVariables,
  SendDeviceEnrollmentTokenEmailDocument,
  SendDeviceEnrollmentTokenEmailMutation,
  SendDeviceEnrollmentTokenEmailMutationVariables,
} from "../../generated/graphql";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { client } from "../../lib/apolloClient";
import { formatDateTimeInline } from "../../lib/dateUtils";
import { canRevealToken, formatTokenUses, getScopeTypeLabel } from "../../lib/deviceEnrollment";
import { borderRadius, color, space, typography } from "../../theme/designTokens";
import { dialogContentSx, dialogPaperSx, dialogTitleSx } from "../../theme/dialogStyles";
import type { DeviceEnrollmentEmailDeliveryRecord, DeviceEnrollmentTokenRecord } from "../../types/deviceEnrollment";
import { printEnrollmentToken } from "./printEnrollmentToken";

export type DeviceEnrollmentTokenDialogMode = "created" | "view";

export type EnrollmentTokenSecret = {
  code: string;
  qrPayload: string;
};

type Props = {
  open: boolean;
  mode: DeviceEnrollmentTokenDialogMode;
  token: DeviceEnrollmentTokenRecord | null;
  /* The plain secret returned at creation. Later views fetch it separately. */
  secret?: EnrollmentTokenSecret | null;
  onClose: () => void;
};

const QR_SIZE = 168;

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const DeviceEnrollmentTokenDialog = ({ open, mode, token, secret, onClose }: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const handleError = useErrorHandler();
  const [locale] = useLocaleState();

  const [recipientEmail, setRecipientEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [deliveries, setDeliveries] = useState<DeviceEnrollmentEmailDeliveryRecord[]>([]);

  useEffect(() => {
    setDeliveries(token?.emailDeliveries ?? []);
    setRecipientEmail("");
  }, [token]);

  const [loadedSecret, setLoadedSecret] = useState<(EnrollmentTokenSecret & { tokenId: string }) | null>(null);
  const reportRevealError = useRef<(error: unknown) => void>(() => undefined);
  reportRevealError.current = error =>
    handleError(error, translate("deviceEnrollment.detail.revealError"), { operation: "revealDeviceEnrollmentToken" });
  const tokenId = token?.id;
  const canReveal = !!token && canRevealToken(token);

  useEffect(() => {
    let active = true;
    setLoadedSecret(null);
    if (!open || !tokenId || !canReveal || secret) return;
    const loadSecret = async () => {
      try {
        const result = await client.query<RevealDeviceEnrollmentTokenQuery, RevealDeviceEnrollmentTokenQueryVariables>({
          query: RevealDeviceEnrollmentTokenDocument,
          variables: { id: tokenId },
          fetchPolicy: "no-cache",
        });
        const revealedToken = result.data.deviceEnrollmentToken;
        if (active && revealedToken?.code && revealedToken.qrPayload) {
          setLoadedSecret({ tokenId, code: revealedToken.code, qrPayload: revealedToken.qrPayload });
        }
      } catch (error) {
        if (active) reportRevealError.current(error);
      }
    };
    void loadSecret();
    return () => {
      active = false;
    };
  }, [open, tokenId, canReveal, secret]);

  const revealed = open && canReveal ? (secret ?? (loadedSecret?.tokenId === tokenId ? loadedSecret : null)) : null;

  const scopeLabel = token
    ? token.scopeLocation
      ? `${translate(getScopeTypeLabel(token.scopeType))} · ${token.scopeLocation.name}`
      : translate(getScopeTypeLabel(token.scopeType))
    : "";
  const expiresLabel = token ? formatDateTimeInline(token.expiresAt, locale) : "";
  const usageLabel = token ? formatTokenUses(token) : "";

  const summaryRows = useMemo(
    () => [
      { label: translate("deviceEnrollment.column.scope"), value: scopeLabel },
      { label: translate("deviceEnrollment.column.expiresAt"), value: expiresLabel },
      { label: translate("deviceEnrollment.usage.label"), value: usageLabel },
    ],
    [expiresLabel, scopeLabel, translate, usageLabel]
  );

  const handleCopy = useCallback(async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.code);
      notify("deviceEnrollment.detail.copySuccess", { type: "success" });
    } catch (error) {
      handleError(error, translate("deviceEnrollment.detail.copyError"), { operation: "copyEnrollmentCode" });
    }
  }, [handleError, notify, revealed, translate]);

  const handlePrint = useCallback(() => {
    if (!revealed) return;
    printEnrollmentToken({
      title: translate("deviceEnrollment.detail.printTitle"),
      code: revealed.code,
      qrSvgMarkup: renderToStaticMarkup(<QRCodeSVG value={revealed.qrPayload} size={220} />),
      rows: summaryRows,
      instructions: translate("deviceEnrollment.detail.instructions"),
    });
  }, [revealed, summaryRows, translate]);

  const canSend = token?.status === DeviceEnrollmentTokenStatus.Active && !!revealed;

  const handleSend = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!token || !isValidEmail(recipientEmail) || isSending) return;

      setIsSending(true);
      try {
        const result = await client.mutate<
          SendDeviceEnrollmentTokenEmailMutation,
          SendDeviceEnrollmentTokenEmailMutationVariables
        >({
          mutation: SendDeviceEnrollmentTokenEmailDocument,
          variables: { input: { tokenId: token.id, recipientEmail: recipientEmail.trim() } },
        });
        const delivery = result.data?.sendDeviceEnrollmentTokenEmail;
        if (delivery) setDeliveries(current => [delivery, ...current]);
        setRecipientEmail("");
        notify("deviceEnrollment.detail.emailSuccess", { type: "success" });
      } catch (error) {
        handleError(error, translate("deviceEnrollment.detail.emailError"), {
          operation: "sendDeviceEnrollmentTokenEmail",
        });
      } finally {
        setIsSending(false);
      }
    },
    [handleError, isSending, notify, recipientEmail, token, translate]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
      data-testid="device-enrollment-token-dialog"
    >
      <DialogTitle sx={{ ...dialogTitleSx, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }}>
          {translate(mode === "created" ? "deviceEnrollment.detail.createdTitle" : "deviceEnrollment.detail.title")}
        </Typography>
        <IconButton
          aria-label={translate("ra.action.close")}
          onClick={onClose}
          data-testid="device-enrollment-token-dialog-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ ...dialogContentSx, display: "grid", gap: 3 }}>
        {revealed ? (
          <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
            <Box
              sx={{
                flexShrink: 0,
                p: 1.5,
                border: `1px solid ${color.panelBorder}`,
                borderRadius: borderRadius.md,
                backgroundColor: color.white,
              }}
              data-testid="device-enrollment-token-qr"
            >
              <QRCodeSVG
                value={revealed.qrPayload}
                size={QR_SIZE}
                aria-label={translate("deviceEnrollment.detail.qrAlt")}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box>
                <Typography variant="body2" color={color.gray}>
                  {translate("deviceEnrollment.detail.codeLabel")}
                </Typography>
                <Typography
                  data-testid="device-enrollment-token-code"
                  sx={{
                    fontSize: 34,
                    fontWeight: typography.fontWeight.semibold,
                    letterSpacing: "4px",
                    lineHeight: 1.2,
                  }}
                >
                  {revealed.code}
                </Typography>
              </Box>
              <SummaryRows rows={summaryRows} />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  color="secondary"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                  data-testid="device-enrollment-token-copy"
                >
                  {translate("deviceEnrollment.action.copyCode")}
                </Button>
                <Button
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  data-testid="device-enrollment-token-print"
                >
                  {translate("deviceEnrollment.action.print")}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color={color.gray} data-testid="device-enrollment-token-code-hidden">
              {translate("deviceEnrollment.detail.codeHidden")}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <SummaryRows rows={summaryRows} />
            </Box>
          </Box>
        )}

        {canSend && (
          <Box
            component="form"
            onSubmit={handleSend}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pt: 2,
              borderTop: `1px solid ${color.datagridBorder}`,
            }}
          >
            <Typography fontWeight={typography.fontWeight.medium}>
              {translate("deviceEnrollment.detail.emailTitle")}
            </Typography>
            <Typography variant="body2" color={color.gray}>
              {translate("deviceEnrollment.detail.emailDescription")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                type="email"
                size="small"
                fullWidth
                label={translate("deviceEnrollment.detail.emailLabel")}
                value={recipientEmail}
                onChange={event => setRecipientEmail(event.target.value)}
                inputProps={{ "data-testid": "device-enrollment-token-email-input" }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!isValidEmail(recipientEmail) || isSending}
                startIcon={isSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                sx={{ backgroundColor: color.primary, color: color.white, whiteSpace: "nowrap" }}
                data-testid="device-enrollment-token-email-send"
              >
                {translate("deviceEnrollment.action.send")}
              </Button>
            </Box>
          </Box>
        )}

        {deliveries.length > 0 && (
          <Box data-testid="device-enrollment-token-deliveries">
            <Typography fontWeight={typography.fontWeight.medium} sx={{ mb: 0.5 }}>
              {translate("deviceEnrollment.detail.deliveriesTitle")}
            </Typography>
            {deliveries.map(delivery => (
              <Typography key={delivery.id} variant="body2">
                {delivery.recipientEmail} · {translate(`deviceEnrollment.deliveryStatus.${delivery.status}`)} ·{" "}
                {formatDateTimeInline(delivery.sentAt ?? delivery.failedAt ?? delivery.queuedAt, locale)}
                {delivery.sentBy?.name ? ` · ${delivery.sentBy.name}` : ""}
              </Typography>
            ))}
          </Box>
        )}

        {token && (
          <Box data-testid="device-enrollment-token-redemptions">
            <Typography fontWeight={typography.fontWeight.medium} sx={{ mb: 0.5 }}>
              {translate("deviceEnrollment.detail.redemptionsTitle")}
            </Typography>
            {token.redemptions.length === 0 ? (
              <Typography variant="body2" color={color.gray}>
                {translate("deviceEnrollment.detail.noRedemptions")}
              </Typography>
            ) : (
              token.redemptions.map(redemption => (
                <Typography key={redemption.id} variant="body2">
                  {redemption.device ? (
                    <Link to={`/Device/${redemption.device.id}/show`}>{redemption.device.name}</Link>
                  ) : (
                    translate("deviceEnrollment.detail.redemptionDeleted")
                  )}
                  {redemption.targetLocation ? ` · ${redemption.targetLocation.name}` : ""}
                  {` · ${formatDateTimeInline(redemption.redeemedAt, locale)}`}
                </Typography>
              ))
            )}
          </Box>
        )}

        {revealed && (
          <Typography
            variant="body2"
            sx={{
              backgroundColor: color.primarySurface,
              borderRadius: borderRadius.md,
              p: `${space.lg}px ${space.xl}px`,
            }}
          >
            {translate("deviceEnrollment.detail.instructions")}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ backgroundColor: color.primary, color: color.white }}
          data-testid="device-enrollment-token-done"
        >
          {translate("deviceEnrollment.action.done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SummaryRows = ({ rows }: { rows: Array<{ label: string; value: string }> }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "minmax(110px, auto) 1fr", columnGap: 2, rowGap: 0.5 }}>
    {rows.map(row => (
      <Box key={row.label} sx={{ display: "contents" }}>
        <Typography variant="body2" color={color.gray}>
          {row.label}
        </Typography>
        <Typography variant="body2">{row.value}</Typography>
      </Box>
    ))}
  </Box>
);
